import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as Trash } from '../../icons/trash.svg';
import { ReactComponent as ArrowLeft } from '../../icons/arrow-left.svg';
import { ReactComponent as ArrowRight } from '../../icons/arrow-right.svg';
import { ReactComponent as AngleDown } from '../../icons/angle-down.svg';
import './TableFooter.css';

function TableFooter({ 
  checkedItems = [], 
  onDeleteSelected, 
  currentPage = 1, 
  totalPages = 1, 
  onPageChange,
  maxVisibleButtons = 4
}) {
  const [isPageInputOpen, setIsPageInputOpen] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const safeTotalPages = Math.max(1, totalPages);

  const getPaginationItems = () => {
    const boundaryCount = 2;
    const siblingCount = Math.max(1, Math.floor((maxVisibleButtons - 2) / 2));
    const includedPages = new Set();

    for (let i = 1; i <= Math.min(boundaryCount, safeTotalPages); i += 1) {
      includedPages.add(i);
    }
    for (
      let i = Math.max(1, safeTotalPages - boundaryCount + 1);
      i <= safeTotalPages;
      i += 1
    ) {
      includedPages.add(i);
    }

    const aroundStart = Math.max(1, currentPage - siblingCount);
    const aroundEnd = Math.min(safeTotalPages, currentPage + siblingCount);
    for (let i = aroundStart; i <= aroundEnd; i += 1) {
      includedPages.add(i);
    }

    const orderedPages = [...includedPages].sort((a, b) => a - b);
    const items = [];

    orderedPages.forEach((page, index) => {
      if (index > 0 && page - orderedPages[index - 1] > 1) {
        items.push(`ellipsis-${orderedPages[index - 1]}-${page}`);
      }
      items.push(page);
    });

    return items;
  };

  const paginationItems = getPaginationItems();

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < safeTotalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Handle clicking on "Strona" to open input
  const handleStronaClick = () => {
    setIsPageInputOpen(true);
    setPageInputValue('');
  };

  // Handle input change
  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  // Handle input submit (Enter key or blur)
  const handlePageInputSubmit = () => {
    const pageNum = parseInt(pageInputValue, 10);
    if (pageNum >= 1 && pageNum <= safeTotalPages) {
      onPageChange(pageNum);
    }
    setIsPageInputOpen(false);
    setPageInputValue('');
  };

  // Handle key press in input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageInputSubmit();
    } else if (e.key === 'Escape') {
      setIsPageInputOpen(false);
      setPageInputValue('');
    }
  };

  // Focus input when opened
  useEffect(() => {
    if (isPageInputOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isPageInputOpen]);

  // Close input when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsPageInputOpen(false);
        setPageInputValue('');
      }
    };

    if (isPageInputOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPageInputOpen]);

  return (
    <div className="table-footer">
      {/* Left side - Delete selected button */}
      <button 
        className="delete-selected-btn"
        onClick={onDeleteSelected}
        disabled={checkedItems.length === 0}
      >
        <Trash className="delete-icon" />
        <span>Usuń zaznaczone</span>
      </button>

      {/* Right side - Pagination */}
      <div className="pagination-container">
        {/* Pagination buttons */}
        <div className="pagination-buttons">
          {/* Previous arrow */}
          <button 
            className="pagination-btn pagination-arrow pagination-arrow-left"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            <ArrowLeft className="pagination-arrow-icon" />
          </button>

          {/* Page numbers */}
          {paginationItems.map((item) => {
            if (typeof item === 'string' && item.startsWith('ellipsis-')) {
              return (
                <span key={item} className="pagination-ellipsis" aria-hidden="true">
                  ...
                </span>
              );
            }

            return (
              <button
                key={item}
                className={`pagination-btn pagination-number ${currentPage === item ? 'active' : ''}`}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            );
          })}

          {/* Next arrow */}
          <button 
            className="pagination-btn pagination-arrow pagination-arrow-right"
            onClick={handleNextPage}
            disabled={currentPage === safeTotalPages}
          >
            <ArrowRight className="pagination-arrow-icon" />
          </button>
        </div>

        <div className="pagination-total-pages" aria-live="polite">
          Strona {currentPage} z {safeTotalPages}
        </div>

        {/* Page dropdown/input */}
        <div className="page-dropdown" ref={dropdownRef}>
          {isPageInputOpen ? (
            <div className="page-input-container">
              <span className="page-input-label">Strona</span>
              <input
                ref={inputRef}
                type="text"
                className="page-input"
                value={pageInputValue}
                onChange={handlePageInputChange}
                onKeyDown={handleKeyPress}
                onBlur={handlePageInputSubmit}
                placeholder={`1-${safeTotalPages}`}
                maxLength={String(safeTotalPages).length + 1}
              />
            </div>
          ) : (
            <button className="page-dropdown-btn" onClick={handleStronaClick}>
              <span>Strona</span>
              <AngleDown className="dropdown-icon" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default TableFooter;
